import { AxiosResponse } from 'axios';
import { CardSortByValue } from '../../constants/sort';
import { withUserId, withUserContext } from '../utils';
import {
  B2B_UPDATE_PAYMENT_PATH,
  B2B_BILLING_ADDRESS_PATH,
  B2B_PAYMENTS_RETRIEVE_PATH,
  B2B_PAYPAL_SET_CHECKOUT_PATH,
  B2B_PAYPAL_SAVE_PATH,
  B2B_UPDATE_STATUS_PATH,
  B2B_PAYMENTS_PATH,
  B2B_ADD_UPDATE_NICKNAME_PATH,
  B2B_PAYMENTS_PATH_WITH_USER_ID,
  B2B_STOP_ALL_AUTHORIZATION,
  B2B_ACCOUNT_CNP_ENROLLMENT,
  B2B_GIFT_CARD_BALANCE_PATH,
  B2B_GET_CITI_CARDS,
  B2B_ACCOUNT_RESEND_T2C_PATH,
  B2B_ACCOUNT_HD_WALLET_UNENROLLMENT,
  B2B_ACCOUNT_HD_WALLET_ENROLLMENT,
  B2B_PRO_HOUSE_ACCOUNT_RETRIEVE_PATH,
  B2B_PRO_HOUSE_ACCOUNT_T2C_UNENROLL_PATH,
  B2B_PRO_HOUSE_ACCOUNT_T2C_ENROLL_PATH,
  B2B_ACCOUNT_MULTI_RESEND_T2C_PATH,
  B2B_ADD_CHECKING_ACCOUNT_ENCRYPTED,
  B2B_PRO_HOUSE_ACCOUNT_WITH_USER_ID_SVOC_ID_PATH,
  B2B_PAYPAL_V2_SET_CHECKOUT_PATH,
  B2B_PAYPAL_V2_SAVE_PATH,
  PAYPAL_CANCEL_REDIRECT_PATH,
  PAYPAL_RETURN_PATH,
} from '../../constants/links';
import {
  TB2BCreditCardUpdateResponse,
  IB2BBillingAddressResponse,
  IB2BPaymentCard,
  IB2BPaymentCardsResponse,
  IB2BPaypalInitResponse,
  IB2BCNPEnrollmentResponse,
  IB2BCNPEnrollmentRequest,
  CNP_ENROLLMENT_ACTIONS,
  TB2BPaymentCardRequest,
  TCNPEnrollmentGroups,
  TB2BGiftCardBalanceRequest,
  IB2BHDWalletUnEnrollmentResponse,
  IB2BHDWalletUnEnrollmentRequest,
  IAuthHdWalletRequest,
  IB2BCNPHDWalletAuthResponse,
  IAuthHdWalletWithDetailsRequest,
  IT2CMultiResendInviteRequest,
  IT2CMultiResendInviteResponse,
  TB2BPHAT2CEnrollmentRequest,
  T2CMultipleApproverRequest,
  IB2BAddCheckingAccountRequest,
  TNewPayment,
  TUpdateUsersForTenderRequestBody,
  TUpdateUsersForTenderResponse,
  TAuthorizedUsersByTenderResponse,
} from '../../models/b2b/payments';
import { CardType } from '../../constants/payments';
import { PAYMENTS_PAGE_SIZE } from '../../constants/general';
import { apiB2B } from '../axios-client';
import {
  IPaymentCardCommonData,
  IPaymentCardMandatoryPropsForHDPass,
} from '../../models/common/payments';

// eslint-disable-next-line no-shadow
export enum B2BCreditCardsFilterType {
  nickName = 'nickName',
  cardBrand = 'cardBrand',
  cardLast4Digits = 'cardLast4Digits',
  status = 'status',
  expDate = 'expDate',
  citiID = 'citiID',
  NOTHING = '',
}

export const B2BCreditCardsFilterTypePlaceHolder = {
  [B2BCreditCardsFilterType.nickName]: 'Enter Nickname',
  [B2BCreditCardsFilterType.cardLast4Digits]: 'Enter Last 4 Digits',
  [B2BCreditCardsFilterType.expDate]: 'MM/YY',
  [B2BCreditCardsFilterType.citiID]: 'Enter Last 4 Digits',
  [B2BCreditCardsFilterType.cardBrand]: 'Choose a Card Type',
  [B2BCreditCardsFilterType.status]: 'Choose a Status',
  [B2BCreditCardsFilterType.NOTHING]: '',
} as const;

type TypeCD = 'cc' | 'gc' | 'ck' | 'kt' | 'pgc' | 'pba';

export const B2BApiCardType: Record<CardType, TypeCD> = {
  [CardType.CREDIT_CARD]: 'cc',
  [CardType.GIFT_CARD]: 'gc',
  [CardType.CHECKING_ACCOUNT]: 'ck',
  [CardType.KEY_TAG]: 'kt',
  [CardType.PRO_XTRA_DOLLARS]: 'pgc',
  [CardType.PAYPAL_ACCOUNT]: 'pba',
  [CardType.PRO_HOUSE_ACCOUNT]: 'cc',
};

export interface ISortConfig {
  page: number;
  sortBy: CardSortByValue;
  ascendingOrder: boolean;
  pageSize: number;
}

export interface IFilterConfig {
  filterType: B2BCreditCardsFilterType;
  filterValue: string;
}

type Exclusive<T, U> =
  | (T & { [K in keyof U]?: never })
  | (U & { [K in keyof T]?: never });

export type TPhaEnrollT2CParams = {
  paymentId: string;
  t2cPrimary: boolean;
} & Exclusive<
  {
    t2cNumber: string;
    isResend: boolean;
  },
  { t2cApprovers: T2CMultipleApproverRequest }
>;

type TResendT2CParams = {
  t2cNumber: string;
  paymentId: string;
  t2cPrimary?: boolean;
};

export type TPhaUnenrollT2CParams = {
  paymentId: string;
  t2cApprovers?: T2CMultipleApproverRequest;
};

export const b2bPaymentsApi = {
  getPHACards: withUserContext(
    (userId, svocId): Promise<AxiosResponse<IB2BPaymentCardsResponse>> => {
      return apiB2B.get<IB2BPaymentCardsResponse>(
        B2B_PRO_HOUSE_ACCOUNT_RETRIEVE_PATH(userId, svocId),
      );
    },
  ),
  getPayments: withUserContext(
    (
      userId,
      svocId,
      cardType: CardType,
      sortConfig: ISortConfig,
      filterConfig: IFilterConfig,
    ): Promise<AxiosResponse<IB2BPaymentCardsResponse>> => {
      if (filterConfig?.filterType === B2BCreditCardsFilterType.citiID) {
        return apiB2B.get<IB2BPaymentCardsResponse>(
          B2B_GET_CITI_CARDS(userId, svocId, filterConfig.filterValue),
          {
            params: {
              typecd: B2BApiCardType[cardType],
              ps: sortConfig.pageSize || PAYMENTS_PAGE_SIZE,
              pn: sortConfig.page || 1,
              sb: sortConfig.sortBy || 'lastedited',
              asc: sortConfig.ascendingOrder || false,
            },
          },
        );
      }

      return apiB2B.get<IB2BPaymentCardsResponse>(
        B2B_PAYMENTS_RETRIEVE_PATH(userId, svocId),
        {
          params: {
            typecd: B2BApiCardType[cardType],
            ps: sortConfig.pageSize || PAYMENTS_PAGE_SIZE,
            pn: sortConfig.page || 1,
            sb: sortConfig.sortBy || 'lastedited',
            asc: sortConfig.ascendingOrder || false,
            filterType:
              filterConfig.filterType === B2BCreditCardsFilterType.NOTHING
                ? undefined
                : filterConfig.filterType,
            filterValue: filterConfig.filterValue || undefined,
          },
        },
      );
    },
  ),
  getBillingAddress: withUserContext(
    (userId, svocId, addressId: string, isPurchaser: boolean) =>
      apiB2B.get<IB2BBillingAddressResponse>(
        `${B2B_BILLING_ADDRESS_PATH(userId, svocId)}/${addressId}`,
        {
          params: {
            ownerUserId: isPurchaser ? userId : undefined,
          },
        },
      ),
  ),
  paypalInit: withUserId((userId: string) =>
    apiB2B.get<IB2BPaypalInitResponse>(B2B_PAYPAL_SET_CHECKOUT_PATH(userId)),
  ),
  initPayPalV2: withUserId((userId: string) =>
    apiB2B.post<IB2BPaypalInitResponse>(
      B2B_PAYPAL_V2_SET_CHECKOUT_PATH(userId),
      {
        payPalAccountRequest: {
          cancelUrl: PAYPAL_CANCEL_REDIRECT_PATH(),
          returnUrl: PAYPAL_RETURN_PATH(),
          requestType: 'InitPaypal',
        },
      },
    ),
  ),
  paypalSave: withUserId(
    (userId, token: string, paymentCard?: Partial<IB2BPaymentCard>) =>
      apiB2B.post(B2B_PAYPAL_SAVE_PATH(userId), {
        savePayPalAccountRequest: {
          token,
          paymentCard,
        },
      }),
  ),
  savePayPalV2: withUserId(
    (userId, token: string, paymentCard?: Partial<IB2BPaymentCard>) =>
      apiB2B.post(B2B_PAYPAL_V2_SAVE_PATH(userId), {
        payPalAccountRequest: {
          token,
          requestType: 'VerifyAndSave',
          paymentCard,
        },
      }),
  ),
  updatePayment: withUserId(
    (userId, payment: IB2BPaymentCard, typeConfig: CardType) => {
      const payload: TB2BPaymentCardRequest<typeof payment> = {
        paymentCardRequest: {
          paymentCard: payment,
        },
      };
      return apiB2B.put<TB2BCreditCardUpdateResponse>(
        B2B_UPDATE_PAYMENT_PATH(userId),
        payload,
        {
          params: {
            typecd: B2BApiCardType[typeConfig],
          },
        },
      );
    },
  ),
  addOrUpdateCardNickName: withUserId(
    (userId, payment: IB2BPaymentCard, typeConfig: CardType) => {
      const payload: TB2BPaymentCardRequest<typeof payment> = {
        paymentCardRequest: {
          paymentCard: payment,
        },
      };
      return apiB2B.put<TB2BCreditCardUpdateResponse>(
        B2B_ADD_UPDATE_NICKNAME_PATH(userId),
        payload,
        {
          params: {
            typecd: B2BApiCardType[typeConfig],
          },
        },
      );
    },
  ),
  getGiftCardBalance: withUserContext(
    (
      userId,
      svocId,
      selectedGiftCard: IB2BPaymentCard,
      pin: string,
      recaptcha: string,
    ) => {
      const payload: TB2BGiftCardBalanceRequest<typeof selectedGiftCard> = {
        paymentCardRequest: {
          paymentCard: {
            ...selectedGiftCard,
            pin,
            recaptcha,
          },
        },
      };

      return apiB2B.post(B2B_GIFT_CARD_BALANCE_PATH(userId, svocId), payload);
    },
  ),
  updateStatus: withUserId(
    (
      userId,
      payment: Pick<IB2BPaymentCard, 'cardStatus' | 'paymentId'>,
      typeConfig: CardType,
    ) => {
      const payload: TB2BPaymentCardRequest<typeof payment> = {
        paymentCardRequest: {
          paymentCard: payment,
        },
      };
      return apiB2B.put<boolean>(B2B_UPDATE_STATUS_PATH(userId), payload, {
        params: {
          typecd: B2BApiCardType[typeConfig],
        },
      });
    },
  ),
  deletePayment: withUserContext(
    (
      userId,
      svocId,
      paymentId: IB2BPaymentCard['paymentId'],
      typeConfig: CardType,
    ) => {
      return apiB2B.delete<boolean>(
        `${B2B_PAYMENTS_PATH(userId, svocId)}/${paymentId}`,
        {
          params: {
            typecd: B2BApiCardType[typeConfig],
          },
        },
      );
    },
  ),
  addPayment: withUserId(
    (
      userId,
      payment: TNewPayment,
      typeConfig: CardType,
      recaptchaToken?: string | undefined,
    ) => {
      const payload: TB2BPaymentCardRequest<typeof payment> = {
        paymentCardRequest: {
          paymentCard: payment,
        },
      };
      const params = {
        params: {
          typecd: B2BApiCardType[typeConfig],
        },
      };
      const config = recaptchaToken
        ? {
            headers: {
              'X-PAA-AUTH': recaptchaToken,
            },
            ...params,
          }
        : { ...params };

      return apiB2B.post(
        `${B2B_PAYMENTS_PATH_WITH_USER_ID(userId)}/add`,
        payload,
        config,
      );
    },
  ),
  addCheckingAccountWithEncryption: withUserContext(
    (
      userId,
      svocId,
      {
        cardNickName,
        encryptedAccountNumber,
        routingNumber,
      }: IB2BAddCheckingAccountRequest['addCheckingAccountPaymentCardRequest'],
    ) => {
      const payload: IB2BAddCheckingAccountRequest = {
        addCheckingAccountPaymentCardRequest: {
          cardNickName,
          encryptedAccountNumber,
          routingNumber,
        },
      };

      return apiB2B.post(
        B2B_ADD_CHECKING_ACCOUNT_ENCRYPTED(userId, svocId),
        payload,
      );
    },
  ),
  stopAllAuthorization: withUserContext((userId, svocId: string) => {
    return apiB2B.delete(`${B2B_STOP_ALL_AUTHORIZATION(userId, svocId)}`);
  }),
  exportPaymentCardsToCSV: withUserContext(
    (userId, svocId, typeConfig: CardType, fileName: string) => {
      return apiB2B.get<string>(`${B2B_PAYMENTS_PATH(userId, svocId)}/csv/`, {
        params: {
          typecd: B2BApiCardType[typeConfig],
          fileName,
        },
        responseType: 'blob',
      });
    },
  ),
  printPaymentCardsToPDF: withUserContext(
    (userId, svocId, typeConfig: CardType, fileName: string) => {
      return apiB2B.get<string>(`${B2B_PAYMENTS_PATH(userId, svocId)}/pdf/`, {
        params: {
          typecd: B2BApiCardType[typeConfig],
          fileName,
        },
        responseType: 'blob',
      });
    },
  ),
  cnpEnrollmentRemoveAuth: withUserContext(
    (
      userId,
      svocId,
      paymentId: IB2BPaymentCard['paymentId'],
      group: TCNPEnrollmentGroups,
    ) => {
      const payload = {
        cnpEnrollmentRequest: {
          paymentCard: {
            paymentId: paymentId || '',
          },
          cnpEnrollmentActions: CNP_ENROLLMENT_ACTIONS[group],
        },
      };

      return apiB2B.put<
        IB2BCNPEnrollmentResponse,
        AxiosResponse<IB2BCNPEnrollmentResponse>,
        IB2BCNPEnrollmentRequest
      >(B2B_ACCOUNT_CNP_ENROLLMENT(userId, svocId), payload);
    },
  ),
  unEnrollmentHDWallet: withUserContext(
    (userId, svocId, card: IPaymentCardCommonData) => {
      const payload = {
        paymentCardRequest: {
          paymentCard: {
            paymentId: card.paymentId,
          },
        },
      };

      return apiB2B.put<
        IB2BHDWalletUnEnrollmentResponse,
        AxiosResponse<IB2BHDWalletUnEnrollmentResponse>,
        IB2BHDWalletUnEnrollmentRequest
      >(B2B_ACCOUNT_HD_WALLET_UNENROLLMENT(userId, svocId), payload);
    },
  ),
  getPaymentById: withUserContext(
    (
      userId,
      svocId,
      paymentId: string,
      cardType: CardType,
    ): Promise<AxiosResponse<IPaymentCardMandatoryPropsForHDPass>> => {
      return apiB2B.get(`${B2B_PAYMENTS_PATH(userId, svocId)}/${paymentId}`, {
        params: {
          typecd: B2BApiCardType[cardType],
        },
      });
    },
  ),
  hdPassAuthRequest: withUserContext(
    (
      userId,
      svocId,
      data: IAuthHdWalletRequest | IAuthHdWalletWithDetailsRequest,
      recaptchaToken: string,
      deviceid: string,
    ) => {
      return apiB2B.put<IB2BCNPHDWalletAuthResponse>(
        B2B_ACCOUNT_HD_WALLET_ENROLLMENT(userId, svocId),
        data,
        recaptchaToken
          ? {
              headers: { 'X-PAA-AUTH': recaptchaToken, deviceid },
            }
          : undefined,
      );
    },
  ),

  phaUnenrollT2C: withUserId(
    (
      userId,
      { paymentId, t2cApprovers }: TPhaUnenrollT2CParams,
    ): Promise<AxiosResponse<boolean>> => {
      return apiB2B.put(B2B_PRO_HOUSE_ACCOUNT_T2C_UNENROLL_PATH(userId), {
        paymentCardRequest: {
          paymentCard: {
            paymentId,
          },
          t2cApprovers,
        },
      });
    },
  ),

  // this method now accepts t2cNumber or t2cApprovers but not both
  phaEnrollT2C: withUserId(
    (
      userId,
      {
        t2cNumber,
        paymentId,
        t2cPrimary,
        isResend,
        t2cApprovers,
      }: TPhaEnrollT2CParams,
    ): Promise<AxiosResponse<boolean>> => {
      return apiB2B.post<
        boolean,
        AxiosResponse<boolean>,
        TB2BPHAT2CEnrollmentRequest
      >(
        B2B_PRO_HOUSE_ACCOUNT_T2C_ENROLL_PATH(userId),
        {
          paymentCardRequest: {
            paymentCard: {
              t2cNumber,
              paymentId,
              t2cPrimary,
            },
            t2cApprovers,
          },
        },
        {
          params: {
            isResend,
          },
        },
      );
    },
  ),

  multiResendT2CRequest: withUserId(
    (userId, data: IT2CMultiResendInviteRequest) => {
      return apiB2B.post<
        IT2CMultiResendInviteResponse,
        AxiosResponse<IT2CMultiResendInviteResponse>,
        IT2CMultiResendInviteRequest
      >(B2B_ACCOUNT_MULTI_RESEND_T2C_PATH(userId), data);
    },
  ),
};

export const resendT2CRequest = withUserId(
  (
    userId,
    { t2cNumber, paymentId, t2cPrimary }: TResendT2CParams,
  ): Promise<AxiosResponse<boolean>> =>
    apiB2B.post(B2B_ACCOUNT_RESEND_T2C_PATH(userId), {
      paymentCardRequest: {
        paymentCard: {
          t2cNumber,
          paymentId,
          t2cPrimary,
        },
      },
    }),
);

export const getUsersForTenderRequest = withUserContext(
  (userId, svocId, paymentId: string) => {
    return apiB2B
      .get<TAuthorizedUsersByTenderResponse>(
        `${B2B_PRO_HOUSE_ACCOUNT_WITH_USER_ID_SVOC_ID_PATH(userId, svocId)}/${paymentId}/authorized-users`,
      )
      .then(({ data }) => data);
  },
);

export const updateUsersForTenderRequest = withUserContext(
  (
    userId,
    svocId,
    paymentId: string,
    request: TUpdateUsersForTenderRequestBody,
  ) => {
    return apiB2B
      .post<
        TUpdateUsersForTenderResponse,
        AxiosResponse<TUpdateUsersForTenderResponse>,
        TUpdateUsersForTenderRequestBody
      >(`${B2B_PRO_HOUSE_ACCOUNT_WITH_USER_ID_SVOC_ID_PATH(userId, svocId)}/${paymentId}/authorized-users`, request)
      .then(({ data }) => {
        return data;
      });
  },
);

export const cnpEnrollmentRequest = withUserContext(
  (userId, svocId, data: IB2BCNPEnrollmentRequest, recaptchaToken?: string) => {
    return apiB2B.put<
      IB2BCNPEnrollmentResponse,
      AxiosResponse<IB2BCNPEnrollmentResponse>,
      IB2BCNPEnrollmentRequest
    >(
      B2B_ACCOUNT_CNP_ENROLLMENT(userId, svocId),
      data,
      recaptchaToken
        ? {
            headers: { 'X-PAA-AUTH': recaptchaToken },
          }
        : undefined,
    );
  },
);
